import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import GreenButton from '../Components/Buttons/Greenbutton';
import { useAuth } from '../context/AuthContext';
import { createAppointment, getAppointments } from '../lib/api';

function formatTimeLabel(totalMinutes) {
  const hours24 = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const period = hours24 >= 12 ? 'PM' : 'AM';
  const hours12 = hours24 % 12 || 12;
  return `${String(hours12).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${period}`;
}

function createAppointmentTimes() {
  const slots = [];
  const startMinutes = 10 * 60;
  const endMinutes = 18 * 60;
  const intervalMinutes = 30;

  for (let current = startMinutes; current < endMinutes; current += intervalMinutes) {
    const next = current + intervalMinutes;
    slots.push(`${formatTimeLabel(current)} - ${formatTimeLabel(next)}`);
  }

  return slots;
}

const appointmentTimes = createAppointmentTimes();

function AppointmentPage() {
  const { user } = useAuth();
  const hasUserId = Boolean(user?.id);
  const [appointments, setAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(() => hasUserId);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [form, setForm] = useState({
    fullName: user?.fullName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    appointmentDate: '',
    appointmentTime: '',
    notes: '',
  });

  useEffect(() => {
    let isMounted = true;

    async function loadAppointments() {
      try {
        const nextAppointments = await getAppointments({ userId: user.id });
        if (isMounted) {
          setAppointments(nextAppointments);
        }
      } catch (loadError) {
        if (isMounted) {
          setError(loadError.message);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    if (user?.id) {
      setIsLoading(true);
      loadAppointments();
    } else {
      // No login: allow booking, but don't try to load user-specific history.
      setIsLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [user?.id]);

  async function handleSubmit(event) {
    event.preventDefault();
    setIsSaving(true);
    setError('');
    setNotice('');

    try {
      const response = await createAppointment({
        userId: user?.id,
        appointment: {
          ...form,
          timezone: 'Asia/Kathmandu',
        },
      });

      setAppointments((current) => [response.appointment, ...current]);
      setNotice('Appointment booked successfully.');
      setForm((current) => ({
        ...current,
        appointmentDate: '',
        appointmentTime: '',
        notes: '',
      }));
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <section className="page-card appointment-page">
      <div className="appointment-hero">
        <p className="appointment-hero__crumb">Home / Appointment / Book</p>
        <h1 className="page-card__title">Book Appointment</h1>
        <p className="page-card__text">
          Schedule your consultation, choose a convenient date and time, and keep your
          booking details in one clear place.
        </p>
      </div>

      <div className="appointment-card">
        <div className="appointment-card__icon">+</div>
        <div>
          <h2>Dr. Prajwal Sedhai</h2>
          <p>Ganeshman Singh Hospital, Nepal</p>
          <span>Jawalakhel, Lalitpur, Nepal</span>
        </div>
      </div>

      {error ? <p className="products-state products-state--error">{error}</p> : null}
      {notice ? <p className="orders-success">{notice}</p> : null}

      <div className="appointment-layout">
        <form className="appointment-form" onSubmit={handleSubmit}>
          <div className="appointment-form__header">
            <h2>Select Date & Time</h2>
            <p>Choose your preferred appointment slot</p>
          </div>

          <div className="appointment-form__grid">
            <label className="auth-form__field">
              <span>Full Name</span>
              <input
                required
                autoComplete="name"
                value={form.fullName}
                onChange={(event) => {
                  setForm((current) => ({ ...current, fullName: event.target.value }));
                }}
              />
            </label>
            <label className="auth-form__field">
              <span>Phone Number</span>
              <input
                required
                autoComplete="tel"
                inputMode="tel"
                value={form.phone}
                onChange={(event) => {
                  setForm((current) => ({ ...current, phone: event.target.value }));
                }}
              />
            </label>
            <label className="auth-form__field appointment-form__full">
              <span>Email Address</span>
              <input
                required
                autoComplete="email"
                type="email"
                value={form.email}
                onChange={(event) => {
                  setForm((current) => ({ ...current, email: event.target.value }));
                }}
              />
            </label>
            <label className="auth-form__field">
              <span>Appointment Date</span>
              <input
                required
                type="date"
                value={form.appointmentDate}
                onChange={(event) => {
                  setForm((current) => ({ ...current, appointmentDate: event.target.value }));
                }}
              />
            </label>
            <label className="auth-form__field">
              <span>Appointment Time</span>
              <select
                required
                value={form.appointmentTime}
                onChange={(event) => {
                  setForm((current) => ({ ...current, appointmentTime: event.target.value }));
                }}
              >
                <option value="">Select a time slot</option>
                {appointmentTimes.map((slot) => (
                  <option key={slot} value={slot}>
                    {slot}
                  </option>
                ))}
              </select>
            </label>
            <label className="auth-form__field appointment-form__full">
              <span>Notes</span>
              <textarea
                rows="4"
                value={form.notes}
                onChange={(event) => {
                  setForm((current) => ({ ...current, notes: event.target.value }));
                }}
              />
            </label>
          </div>

          <div className="appointment-form__timezone">Time zone: Nepal Time (NPT)</div>
          <div className="appointment-form__actions">
            <GreenButton
              type="submit"
              disabled={isSaving}
              text={isSaving ? 'Booking...' : 'Confirm Appointment'}
            />
            <button
              type="button"
              className="appointment-form__reset"
              onClick={() => {
                setForm({
                  fullName: user?.fullName || '',
                  email: user?.email || '',
                  phone: user?.phone || '',
                  appointmentDate: '',
                  appointmentTime: '',
                  notes: '',
                });
              }}
            >
              Reset Form
            </button>
          </div>
        </form>

        <aside className="appointment-history">
          <div className="appointment-form__header">
            <h2>Your Appointments</h2>
            <p>Upcoming and past bookings</p>
          </div>
          {isLoading ? <p className="products-state">Loading appointments...</p> : null}
          {!hasUserId && appointments.length === 0 ? (
            <p className="products-state">Login to view your appointment history.</p>
          ) : null}
          {!isLoading && hasUserId && appointments.length === 0 ? (
            <p className="products-state">No appointments booked yet.</p>
          ) : null}
          <div className="appointment-history__list">
            {appointments.map((appointment) => (
              <article key={appointment.id} className="appointment-history__item">
                <strong>{appointment.appointmentDate}</strong>
                <span>{appointment.appointmentTime}</span>
                <span>{appointment.status}</span>
              </article>
            ))}
          </div>
          <p className="auth-form__switch">
            Need products too? <Link to="/products">Continue shopping</Link>
          </p>
        </aside>
      </div>
    </section>
  );
}

export default AppointmentPage;
